import firebase from "firebase/app";
import "firebase/functions";
import {
  firebaseConfig,
  functionsEmulatorURL,
  functionsRegion,
} from "src/config/firebase";

/**
 * Accountant Access API module.
 *
 * Reseller-side callables (invite / get / revoke) are authenticated with the
 * reseller's Firebase ID token (`context.auth`) on the backend — no token is
 * passed in the payload.
 *
 * Invitee / accountant-portal callables (status / accept / list) are used by
 * the accountant flows in later increments; their thin wrappers live here so
 * the whole feature talks to one module.
 *
 * All writes go through Cloud Functions — there are no direct Firestore client
 * writes to `Invitations` (see firestore.rules: Admin-SDK only).
 */

export type AccountantStatus = "active" | "pending";

/** A reseller's view of their single accountant relationship. */
export interface AccountantInvitation {
  /** Invitation document id. */
  id: string;
  /** Accountant's email address. */
  accountant: string;
  /** `active` once accepted, otherwise `pending`. */
  status: AccountantStatus;
  /** Epoch millis the invite was created / accountant added. */
  createdAt: number;
  /** Epoch millis the invite was accepted (only when `status === "active"`). */
  acceptedAt?: number;
}

/**
 * Reseller: create an invitation for `email`, or resend if one already exists
 * (backend decides email-only vs delete+recreate based on expiry).
 */
export async function inviteAccountant(
  email: string
): Promise<AccountantInvitation> {
  const fn = firebase.functions().httpsCallable("accountant-invite");
  const { data } = await fn({ email });
  return data;
}

/**
 * Reseller: fetch the current accountant relationship, or `null` when none has
 * been invited yet (drives the empty state in Profile settings).
 */
export async function getAccountantInvitation(): Promise<AccountantInvitation | null> {
  const fn = firebase.functions().httpsCallable("accountant-getInvitation");
  const { data } = await fn();
  return data ?? null;
}

/** Reseller: revoke the accountant's access immediately. */
export async function revokeAccountant(): Promise<{ success: boolean }> {
  const fn = firebase.functions().httpsCallable("accountant-revoke");
  const { data } = await fn();
  return data;
}

// ---------------------------------------------------------------------------
// Invitee / accountant-portal callables (wired up in later increments).
// ---------------------------------------------------------------------------

export interface InvitationStatus {
  /** Display name for the acceptance page: company name or owner email. */
  inviterName: string;
  /**
   * Masked invitee email (e.g. `j***@firm.com`). Shown as a hint; existing
   * accountants type their full address to sign in.
   */
  accountantEmail: string;
  /** Whether a `Support_Users` accountant already exists for that email. */
  accountantExists: boolean;
  /** True when the invite is expired or already accepted (not actionable). */
  expired: boolean;
  accepted: boolean;
}

/** Public: resolve an invitation id to its status (drives accept-page branch). */
export async function getInvitationStatus(
  invitationId: string
): Promise<InvitationStatus> {
  const fn = firebase
    .functions()
    .httpsCallable("accountant-getInvitationStatus");
  const { data } = await fn({ invitationId });
  return data;
}

/** Public: a brand-new accountant accepts an invite, creating their login. */
export async function acceptInvitation(
  invitationId: string,
  password: string
): Promise<{ token: string }> {
  const fn = firebase.functions().httpsCallable("accountant-acceptInvitation");
  const { data } = await fn({ invitationId, password });
  return data;
}

/** Authenticated (support JWT): an existing accountant accepts a new invite. */
export async function acceptInvitationWithJwt(
  token: string,
  invitationId: string
): Promise<{ success: boolean }> {
  const fn = firebase
    .functions()
    .httpsCallable("accountant-acceptInvitationWithJwt");
  const { data } = await fn({ token, invitationId });
  return data;
}

/**
 * Authenticated (support JWT): decline a pending invite (deletes the invitation).
 */
export async function rejectInvitationWithJwt(
  token: string,
  invitationId: string
): Promise<{ success: boolean }> {
  const fn = firebase
    .functions()
    .httpsCallable("accountant-rejectInvitationWithJwt");
  const { data } = await fn({ token, invitationId });
  return data;
}

// ---------------------------------------------------------------------------
// Accountant "My Clients" portal.
// ---------------------------------------------------------------------------

/** One client row on the accountant's My Clients page. */
export interface AccountantClient {
  /** Invitation document id (stable key). */
  invitationId: string;
  /** Reseller Firebase uid (target for impersonation), present once accepted. */
  uid?: string;
  /** Reseller's company name, or empty if not set. */
  companyName: string;
  /** True when the reseller has uploaded a company logo (fetch via getClientLogoUrl). */
  hasCompanyLogo: boolean;
  /** Reseller (owner) email — also the impersonation target for generateToken. */
  ownerEmail: string;
  /** `active` once accepted, otherwise `pending`. */
  status: AccountantStatus;
  /** Epoch millis of the last accountant access, or null if never. */
  lastAccessed: number | null;
}

export interface AccountantClientList {
  /** The signed-in accountant's own email (header + empty-state copy). */
  accountantEmail: string;
  clients: AccountantClient[];
}

/** Authenticated (support JWT): list the accountant's clients + their email. */
export async function listAccountantClients(
  token: string
): Promise<AccountantClientList> {
  const fn = firebase.functions().httpsCallable("accountant-listClients");
  const { data } = await fn({ token });
  return data;
}

/**
 * HTTP URL for `accountant-getClientLogo` (img src). Passes the support JWT as
 * `token` so the backend can authorize the accountant + invitation pair.
 */
export function getAccountantClientLogoUrl(
  invitationId: string,
  token: string
): string {
  const params = new URLSearchParams({ invitationId, token });
  const functionName = "accountant-getClientLogo";
  const projectId = firebaseConfig.projectId;
  if (functionsEmulatorURL) {
    return `${functionsEmulatorURL}/${projectId}/${functionsRegion}/${functionName}?${params}`;
  }
  return `https://${functionsRegion}-${projectId}.cloudfunctions.net/${functionName}?${params}`;
}

/** Public: start the accountant password-reset flow (emails a reset link). */
export async function requestAccountantPasswordReset(
  email: string
): Promise<{ success: boolean }> {
  const fn = firebase
    .functions()
    .httpsCallable("accountant-requestPasswordReset");
  const { data } = await fn({ email });
  return data;
}

/** Public: complete the accountant password reset with the emailed token. */
export async function resetAccountantPassword(
  resetToken: string,
  password: string
): Promise<{ success: boolean }> {
  const fn = firebase.functions().httpsCallable("accountant-resetPassword");
  const { data } = await fn({ resetToken, password });
  return data;
}
